import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d77q40rll6jbq2vdcug0';

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const message = data.get('message') as string;
    if (message && message.length > 500) {
      return;
    }

    const params = new URLSearchParams();
    data.forEach((value, key) => {
      params.append(key, value as string);
    });

    setStatus('sending');
    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-slate-900">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 border border-slate-700 rounded-full px-4 py-1.5 mb-5">
            <span className="text-slate-400 text-xs font-semibold tracking-widest uppercase">Get In Touch</span>
          </div>
          <h2
            className="text-4xl md:text-5xl font-bold text-white mb-4"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
          >
            Contact <span className="text-cyan-400">VITEK D.O.O.</span>
          </h2>
          <p className="text-slate-400 text-lg max-w-xl mx-auto">
            Have a question or want to learn more about our products? We&apos;d love to hear from you.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="flex flex-col gap-8">
            <div>
              <h3
                className="text-xl font-bold text-white mb-6"
                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
              >
                Company Information
              </h3>
              <div className="space-y-5">
                {/* Address */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-800 shrink-0">
                    <i className="ri-map-pin-line text-cyan-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 font-semibold uppercase tracking-widest mb-1">Address</p>
                    <p className="text-white text-sm">103 BR. BLOK 28-TIRZ/BUNARDZIK</p>
                    <p className="text-slate-400 text-sm mt-0.5">
                      <span className="text-slate-500">City:</span> SKOPJE &nbsp;|&nbsp;
                      <span className="text-slate-500">Zip:</span> 1000 &nbsp;|&nbsp;
                      <span className="text-slate-500">Country:</span> North Macedonia
                    </p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-800 shrink-0">
                    <i className="ri-mail-line text-cyan-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 font-semibold uppercase tracking-widest mb-1">Email</p>
                    <p className="text-white text-sm">info@vitekdoo.com</p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-slate-800 shrink-0">
                    <i className="ri-phone-line text-cyan-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 font-semibold uppercase tracking-widest mb-1">Number</p>
                    <p className="text-white text-sm">+1 6626855715</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Map placeholder */}
            <div className="rounded-2xl overflow-hidden border border-slate-700 h-52">
              <iframe
                title="VITEK D.O.O. Location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d94985.36!2d21.4254!3d41.9981!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x135415b4b5b5b5b5%3A0x0!2sSkopje%2C%20North%20Macedonia!5e0!3m2!1sen!2smk!4v1"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>

          {/* Contact Form */}
          <div className="bg-slate-800 rounded-3xl p-8 border border-slate-700">
            <h3
              className="text-xl font-bold text-white mb-6"
              style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            >
              Send Us a Message
            </h3>

            {status === 'success' ? (
              <div className="flex flex-col items-center justify-center py-12 gap-4">
                <div className="w-16 h-16 flex items-center justify-center rounded-full bg-cyan-500/20">
                  <i className="ri-check-line text-cyan-400 text-3xl"></i>
                </div>
                <p className="text-white font-semibold text-lg">Message Sent!</p>
                <p className="text-slate-400 text-sm text-center">Thank you for reaching out. We&apos;ll get back to you shortly.</p>
                <button
                  onClick={() => setStatus('idle')}
                  className="mt-2 px-6 py-2 rounded-full border border-slate-600 text-slate-300 text-sm hover:border-slate-400 transition-colors cursor-pointer whitespace-nowrap"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                id="vitek-contact-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="John Doe"
                      className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="you@example.com"
                      className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">
                    Subject
                  </label>
                  <input
                    type="text"
                    name="subject"
                    required
                    placeholder="How can we help?"
                    className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 uppercase tracking-widest mb-2">
                    Message
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="Tell us more about your inquiry..."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full bg-slate-700 border border-slate-600 rounded-xl px-4 py-3 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-cyan-500 transition-colors resize-none"
                  ></textarea>
                  <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-rose-400' : 'text-slate-500'}`}>
                    {charCount}/500
                  </p>
                </div>

                {status === 'error' && (
                  <p className="text-rose-400 text-sm">Something went wrong. Please try again.</p>
                )}

                <button
                  type="submit"
                  disabled={status === 'sending'}
                  className="w-full py-3 rounded-xl bg-cyan-500 hover:bg-cyan-400 disabled:opacity-60 text-white font-semibold text-sm tracking-wide transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  {status === 'sending' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Footer bar */}
        <div className="mt-16 pt-8 border-t border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-slate-500 text-sm">
            &copy; {new Date().getFullYear()} VITEK D.O.O. All rights reserved.
          </p>
          <p className="text-slate-600 text-xs">
            103 BR. BLOK 28-TIRZ/BUNARDZIK, SKOPJE 1000, North Macedonia
          </p>
        </div>
      </div>
    </section>
  );
}
